'use client';

import { useState, useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, ArrowRight, Users, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAtomValue } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';

import { StepsIndicator } from '@/components/glitch-ui/steps-indicator';
import { SourceCampaignPicker } from '@/components/campaign/source-campaign-picker';
import { useWizardHost } from '@/components/campaign/new/wizard-host-context';
import { useLandingPageStep, LandingPageStep } from '@/components/campaign/new/landing-page-step';
import { useToast } from '@/hooks/useToast';
import useLoadingOverlay from '@/hooks/useLoadingOverlay';
import { organizationDataAtom } from '@/store';
import {
  businessQueryOptions,
  campaignsQueryOptions,
  useCreateOptimisationCampaignMutation,
} from '@/lib/query-options';

export const Route = createFileRoute('/_authenticated/new/_layout/campaign/icp')({
  component: IcpCampaign,
});

enum ICP_STEP {
  SELECT_SOURCE = 'SELECT_SOURCE',
  SELECT_LANDING_PAGE = 'SELECT_LANDING_PAGE',
  SELECT_ICPS = 'SELECT_ICPS',
}

export function IcpCampaign() {
  const navigate = useNavigate({ from: Route.fullPath });
  const host = useWizardHost();
  const { toast } = useToast();
  const posthog = usePostHog();
  const organizationData = useAtomValue(organizationDataAtom);

  const { data: business } = useQuery({
    ...businessQueryOptions(organizationData?.id || 0),
    enabled: !!organizationData?.id,
  });

  const { data: campaigns } = useQuery({
    ...campaignsQueryOptions(organizationData?.id),
    enabled: !!organizationData?.id,
  });
  const eligibleSources = (campaigns || []).filter(
    // origin guard (#6381): imported external campaigns can carry goal='prospecting'
    // after classification but were not built by Glitch, so they can't seed a derived campaign
    (c) => c.origin !== 'external' && (c.goal === 'prospecting' || c.goal === 'standard' || c.goal === 'onboarding')
  );

  const icps = business?.icps ?? [];

  const [currentStep, setCurrentStep] = useState<ICP_STEP>(ICP_STEP.SELECT_SOURCE);
  const [sourceCampaignId, setSourceCampaignId] = useState<number | null>(null);
  const [selectedIcpIds, setSelectedIcpIds] = useState<Set<string>>(new Set());
  const [stepValid, setStepValid] = useState(false);

  // Optional landing-page override shared by every fanned-out ICP campaign.
  // The hook defaults the selection to business.business_url; the effect below
  // prefills it from the chosen source campaign's URL once a source is picked.
  const { selectedLandingPage, setSelectedLandingPage } = useLandingPageStep(business);

  // Per-ICP fan-out progress (set when user clicks CREATE).
  const [progress, setProgress] = useState<{ done: number; total: number; failed: string[] } | null>(null);

  const createMutation = useCreateOptimisationCampaignMutation();
  useLoadingOverlay(createMutation.isPending || progress !== null);

  useEffect(() => {
    if (currentStep === ICP_STEP.SELECT_SOURCE) {
      setStepValid(sourceCampaignId !== null);
    } else if (currentStep === ICP_STEP.SELECT_ICPS) {
      setStepValid(selectedIcpIds.size > 0);
    }
    // SELECT_LANDING_PAGE owns its own validity via <LandingPageStep setStepValid>.
  }, [currentStep, sourceCampaignId, selectedIcpIds]);

  // Prefill the landing-page override from the chosen source campaign's URL.
  // A source with an empty URL falls through to the hook's business.business_url
  // default; the guard also means re-selecting the same source never clobbers a
  // manual edit made on the landing-page step.
  const pickedSourceUrl = eligibleSources.find((c) => c.id === sourceCampaignId)?.website_url ?? '';
  useEffect(() => {
    if (pickedSourceUrl.trim()) {
      setSelectedLandingPage(pickedSourceUrl);
    }
  }, [pickedSourceUrl, setSelectedLandingPage]);

  const toggleIcp = (icpId: string) => {
    setSelectedIcpIds((prev) => {
      const next = new Set(prev);
      if (next.has(icpId)) next.delete(icpId);
      else next.add(icpId);
      return next;
    });
  };

  const handleBack = () => {
    if (currentStep === ICP_STEP.SELECT_SOURCE) {
      if (host) {
        host.onExit();
      } else {
        navigate({ to: '/new/campaign' });
      }
      return;
    }
    if (currentStep === ICP_STEP.SELECT_ICPS) {
      setCurrentStep(ICP_STEP.SELECT_LANDING_PAGE);
      return;
    }
    setCurrentStep(ICP_STEP.SELECT_SOURCE);
  };

  const handleNext = async () => {
    if (currentStep === ICP_STEP.SELECT_SOURCE) {
      setCurrentStep(ICP_STEP.SELECT_LANDING_PAGE);
      return;
    }
    if (currentStep === ICP_STEP.SELECT_LANDING_PAGE) {
      setCurrentStep(ICP_STEP.SELECT_ICPS);
      return;
    }
    if (!business?.id || !organizationData?.id || !sourceCampaignId || selectedIcpIds.size === 0) return;

    // Source campaign owns the authoritative business_id. The org-default
    // `business.id` only lines up for single-business orgs.
    const pickedSource = eligibleSources.find((c) => c.id === sourceCampaignId);
    const sourceBusinessId = pickedSource?.business_id ?? business.id;

    const chosenIcps = icps.filter((icp) => selectedIcpIds.has(icp.id));
    posthog?.capture('icp_campaign_creation_submitted', {
      business_id: business.id,
      source_campaign_id: sourceCampaignId,
      icp_count: chosenIcps.length,
    });

    // Fan out: one POST per ICP. Track per-ICP outcome so users see partial
    // success / partial failure instead of an opaque error.
    setProgress({ done: 0, total: chosenIcps.length, failed: [] });
    const failed: string[] = [];
    let done = 0;
    const createdCampaignIds: number[] = [];
    // One UUID per click; per-ICP keys derive from it so re-firing the same
    // click (StrictMode, retries) dedups, but a fresh intentional resubmit
    // gets fresh keys.
    const submissionId = crypto.randomUUID();
    for (const icp of chosenIcps) {
      try {
        const created = await createMutation.mutateAsync({
          organizationId: organizationData.id,
          idempotencyKey: `${submissionId}:${icp.id}`,
          payload: {
            campaign_type: 'icp',
            source_campaign_id: sourceCampaignId,
            business_id: sourceBusinessId,
            // Landing-page override picked in the wizard (defaults to the source
            // campaign's URL); backend uses it as the campaign website_url.
            website_url: selectedLandingPage,
            // Empty -> backend falls back to source campaign's target_locations.
            target_locations: [],
            budget_amount: 0,
            // Backend's _create_icp_keywords requires a non-empty keywords list and
            // creates Keyword rows verbatim from each string (no LLM expansion).
            // Business.icps doesn't carry a `keywords` field, so derive seed terms
            // from icp.name + icp.title — both are short, descriptive labels that
            // pass Google Ads' 30-char Keyword.text limit and PHRASE match-type rules.
            // Long-term fix: have the optimisation service expand seed terms via
            // LLM the way HiPo does, so the wizard can stay anchor-only.
            icp_data: {
              icp_id: icp.id,
              icp_name: icp.name,
              // De-dup so ICPs whose name and title collapse to the same
              // string (after lowercase + 30-char truncation) don't ship
              // duplicate Keyword rows that compete with each other.
              keywords: [
                ...new Set(
                  [icp.name, icp.title]
                    .filter((s): s is string => Boolean(s))
                    .map((s) => s.toLowerCase().slice(0, 30).trim())
                    .filter((s) => s.length > 0)
                ),
              ],
            },
          },
        });
        createdCampaignIds.push(created.campaign_id);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'unknown error';
        failed.push(`${icp.name}: ${msg}`);
      }
      done += 1;
      setProgress({ done, total: chosenIcps.length, failed: [...failed] });
    }
    setProgress(null);

    // Endpoint pre-creates each stub Campaign synchronously, so /campaigns
    // already shows them by the time we land — no refetchWithDelayedUpdates
    // dance needed. Fan-out can produce N campaigns; navigate to /campaigns
    // (the list) instead of any single slug so the user sees them all.
    const successCount = chosenIcps.length - failed.length;
    if (failed.length === 0) {
      if (host) {
        host.onFinished({ campaignIds: createdCampaignIds });
      } else {
        navigate({ to: '/campaigns' });
      }
    } else if (successCount > 0) {
      toast({
        title: 'Some ICP campaigns failed',
        description: `${successCount} started, ${failed.length} failed: ${failed.join('; ')}`,
        variant: 'destructive',
      });
      // Push the user to /campaigns so they can see the ones that succeeded.
      // The failed ICPs stay un-shipped; they can re-pick those from the
      // wizard later if they want to retry.
      if (host) {
        host.onFinished({ campaignIds: createdCampaignIds });
      } else {
        navigate({ to: '/campaigns' });
      }
    } else {
      posthog?.capture('icp_campaign_creation_failed', { error_message: failed.join('; ') });
      toast({
        title: 'Failed to create ICP campaigns',
        description: failed.join('; '),
        variant: 'destructive',
      });
    }
  };

  const stepIndicator =
    currentStep === ICP_STEP.SELECT_SOURCE ? 1 : currentStep === ICP_STEP.SELECT_LANDING_PAGE ? 2 : 3;

  return (
    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 max-w-5xl w-full space-y-6 m-auto pt-8 pb-16">
      <StepsIndicator currentStep={stepIndicator} totalSteps={3} />
      <div className="flex justify-center">
        <div className="w-[60%]">
          <h1 className="text-3xl font-bold text-center mb-2">Create ICP Campaign(s)</h1>
          <p className="text-gray-700 text-center mb-8">
            {currentStep === ICP_STEP.SELECT_SOURCE
              ? 'Select an existing campaign to derive Google Ads context from.'
              : currentStep === ICP_STEP.SELECT_LANDING_PAGE
                ? "Optional: override the landing page these ICP campaigns point to. Defaults to the source campaign's URL."
                : `Pick the customer profiles to target${business?.brand_name ? ` for ${business.brand_name}` : ''}. One campaign is created per profile.`}
          </p>
          <Card className="p-6">
            {currentStep === ICP_STEP.SELECT_SOURCE && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: 'easeInOut' }}
              >
                <SourceCampaignPicker
                  campaigns={eligibleSources}
                  selectedId={sourceCampaignId}
                  onSelect={setSourceCampaignId}
                  emptyState={
                    <div className="text-center py-8 text-gray-500">
                      No eligible source campaigns yet. Create a Prospecting campaign first to use as a source for ICP
                      campaigns.
                    </div>
                  }
                />
              </motion.div>
            )}

            {currentStep === ICP_STEP.SELECT_LANDING_PAGE && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: 'easeInOut' }}
              >
                <LandingPageStep
                  selectedUrl={selectedLandingPage}
                  onSelect={setSelectedLandingPage}
                  setStepValid={setStepValid}
                />
              </motion.div>
            )}

            {currentStep === ICP_STEP.SELECT_ICPS && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: 'easeInOut' }}
                className="space-y-3"
              >
                {icps.length === 0 ? (
                  <div className="text-center py-12 px-4 space-y-4">
                    <Users className="h-12 w-12 text-gray-400 mx-auto" strokeWidth={1.5} />
                    <h3 className="text-lg font-semibold text-gray-900">No customer profiles defined</h3>
                    <p className="text-gray-500">
                      Run brand intel on your business to discover the customer profiles (ICPs) that will power
                      targeted campaigns.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        host?.onLeave();
                        navigate({ to: '/business' });
                      }}
                      className="mt-2"
                    >
                      Go to Brand Intel
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="text-sm text-gray-500 mb-2">
                      {selectedIcpIds.size} of {icps.length} profile{icps.length === 1 ? '' : 's'} selected
                    </div>
                    {icps.map((icp) => {
                      const isSelected = selectedIcpIds.has(icp.id);
                      return (
                        <label
                          key={icp.id}
                          className={`w-full flex items-start space-x-4 p-4 rounded-lg border-2 transition-colors text-left cursor-pointer ${
                            isSelected
                              ? 'border-[#290BD9] bg-[#290BD9]/10'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleIcp(icp.id)}
                            className="mt-0.5"
                          />
                          <div className="flex-1">
                            <div className={`font-medium ${isSelected ? 'text-[#290BD9]' : 'text-gray-900'}`}>
                              {icp.name}
                            </div>
                            {icp.title && <div className="text-sm text-gray-500 mt-1">{icp.title}</div>}
                          </div>
                        </label>
                      );
                    })}
                  </>
                )}
              </motion.div>
            )}

            {progress !== null && (
              <div className="mt-6 p-4 rounded-lg border bg-gray-50">
                <div className="text-sm text-gray-700">
                  Creating {progress.total} ICP campaign{progress.total === 1 ? '' : 's'}: {progress.done} of{' '}
                  {progress.total} dispatched
                </div>
                {progress.failed.length > 0 && (
                  <div className="mt-2 flex items-start gap-2 text-sm text-[#B91C1C]">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>{progress.failed.length} failed: {progress.failed.join('; ')}</div>
                  </div>
                )}
              </div>
            )}
          </Card>

          <div className="flex justify-between items-center pt-6">
            <Button
              variant="outline"
              size="lg"
              onClick={handleBack}
              disabled={progress !== null}
              className="w-[216px] py-6 flex items-center justify-center space-x-2 text-gray-700 hover:text-gray-900 border-gray-200 bg-white hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>BACK</span>
            </Button>
            <Button
              size="lg"
              disabled={!stepValid || createMutation.isPending || progress !== null}
              onClick={handleNext}
              className="w-[216px] py-6 bg-[#290BD9] hover:bg-[#2309C5] shadow-lg flex items-center justify-center space-x-2"
            >
              <span>
                {currentStep === ICP_STEP.SELECT_ICPS
                  ? selectedIcpIds.size > 1
                    ? `CREATE ${selectedIcpIds.size}`
                    : 'CREATE'
                  : 'CONTINUE'}
              </span>
              {currentStep === ICP_STEP.SELECT_ICPS ? (
                <Users className="h-4 w-4" />
              ) : (
                <ArrowRight className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
